import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import ApplicationCard from "../../components/ApplicationCard";
import ApplicationPanel from "../../components/ApplicationPanel";
import NavBar from "../../components/NavBar";
import useAuthStore from "../../store/authStore";
import LoadingSpinner from "../../components/LoadingSpinner";
import ResumeLinkBanner from "../../components/ResumeLinkBanner";
import ConnectToken from "../../components/ConnectToken";
import DashboardEmptyState from "../../components/DashboardEmptyState";


const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "status", label: "Status" },
  { value: "favourites", label: "Favourites" },
];

// ── Confirm delete modal ──────────────────────────────────────

function ConfirmModal({
  title,
  message,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onCancel} />
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-1">
                {title}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium transition"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Accepted offer breakline ──────────────────────────────────

function JobSearchEndBanner({ app }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-green-300 dark:via-green-700 to-transparent" />
      <div className="flex flex-col items-center text-center flex-shrink-0">
        <span className="text-2xl mb-1">🎉</span>
        <p className="text-xs font-semibold text-green-600 dark:text-green-400">
          Job search ended
        </p>
        <p className="text-xs text-gray-400">
          Offer accepted at {app.company} ·{" "}
          {new Date(app.updated_at).toLocaleDateString("en-SE", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </p>
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-green-300 dark:via-green-700 to-transparent" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { seekerUser, clearSeekerAuth } = useAuthStore();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deepLinkTab, setDeepLinkTab] = useState(null);

  const fetchApplications = async () => {
    try {
      const { data } = await api.get(`/applications?sort=${sort}`);
      setApplications(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const openId = searchParams.get("open");
    const tab = searchParams.get("tab") ?? "Details";
    if (!openId || !applications.length) return;
    const app = applications.find((a) => a.id === openId);
    if (app) {
      setExpandedId(app.id);
      setDeepLinkTab(tab);
      setTimeout(() => {
        const el = document.getElementById(`app-${app.id}`);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 300);
    }
  }, [searchParams, applications]);

  useEffect(() => {
    document.title = "Joborigo — My Applications";
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [sort]);

  const handleAdd = () => {
    setEditingApp(null);
    setPanelOpen(true);
  };
  const handleEdit = (app) => {
    setEditingApp(app);
    setPanelOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    await api.delete(`/applications/${deleteTarget.id}`);
    setDeleteTarget(null);
    fetchApplications();
  };

  const handleToggleFavourite = async (app) => {
    await api.patch(`/applications/${app.id}`, {
      is_favourite: !app.is_favourite,
    });
    fetchApplications();
  };

  const handlePanelSave = () => {
    setPanelOpen(false);
    fetchApplications();
  };

  // Find the most recent "Accepted" application to draw the breakline
  const acceptedIdx = applications.findIndex(
    (app) => app.current_status === "Accepted",
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="dashboard" />

      {/* Email verification banner */}
      {seekerUser && !seekerUser.is_verified && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700/30 px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-amber-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Please verify your email address to unlock all features.
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                await api.post(
                  `/auth/resend-verification?email=${seekerUser.email}`,
                );
                alert("Verification email resent — check your inbox.");
              } catch {
                alert("Could not resend — try again later.");
              }
            }}
            className="text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline flex-shrink-0"
          >
            Resend email
          </button>
        </div>
      )}

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">My Applications</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {applications.length} application
              {applications.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition flex items-center gap-2"
          >
            <span className="text-lg leading-none">+</span> Add application
          </button>
        </div>

        {/* Sort tabs */}
        <div className="flex gap-2 mb-6">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                sort === opt.value
                  ? "bg-brand-violet text-white"
                  : "bg-white dark:bg-brand-dark-surface text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* Both cards explain mechanisms that only make sense once there is
            something to track. Showing them to a brand new user pushes the
            one useful action below two paragraphs of setup instructions. */}
        {applications.length > 0 && (
          <>
            <ResumeLinkBanner />
            <ConnectToken />
          </>
        )}
        {/* Application list */}
        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <LoadingSpinner />
          </div>
        ) : applications.length === 0 ? (
          <DashboardEmptyState
            onManual={handleAdd}
            onCreated={fetchApplications}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {applications.map((app, idx) => (
              <div key={app.id}>
                {/* Breakline after accepted offer */}
                {idx === acceptedIdx && acceptedIdx > 0 && (
                  <div className="mb-3">
                    <JobSearchEndBanner app={app} />
                  </div>
                )}

                <div id={`app-${app.id}`}>
                  <ApplicationCard
                    app={app}
                    expanded={expandedId === app.id}
                    defaultTab={expandedId === app.id ? deepLinkTab : null}
                    onToggle={() =>
                      setExpandedId(expandedId === app.id ? null : app.id)
                    }
                    onEdit={() => handleEdit(app)}
                    onDelete={() => setDeleteTarget(app)}
                    onToggleFavourite={() => handleToggleFavourite(app)}
                    onStatusAdded={fetchApplications}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Slide-in panel */}
      <ApplicationPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onSave={handlePanelSave}
        editingApp={editingApp}
      />

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete application?"
          message={`This will permanently remove your application to ${deleteTarget.company} for ${deleteTarget.job_title}. This cannot be undone.`}
          confirmLabel="Delete application"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
